import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAtomValue } from "jotai";
import { Button } from "@common/index";
import { ComponentColor } from "@common/types";
import { useInvitationApi } from "@/hooks/apiHooks/useInvitationApi";
import { useSubmitGuard } from "@/hooks/useSubmitGuard";
import { isProcessingInvitationAtom } from "@/store/invitationAtoms";
import { asApiFailure } from "@utils/apiFailure";
import { captchaErrorMessage } from "../_helpers/captchaErrorMessage";
import { useTurnstile } from "../_helpers/useTurnstile";
import { TurnstileCheck } from "./TurnstileCheck";

const INVALID_EMAIL = "Enter a valid email address";

const emailSchema = z.object({
    email: z
        .string()
        .trim()
        .nonempty("Email address is required")
        .email(INVALID_EMAIL),
});

type EmailFormData = z.infer<typeof emailSchema>;

// OB-02: the API answers every well-formed email with the same body-less 202,
// whether or not it has an application waiting (and, within 10 minutes of the
// last link, sends nothing). So this is the ONLY thing an accepted request may
// say: conditional, never "we've sent you", never the email read back.
const CONFIRMATION =
    "If there's an application waiting for that email, we've sent a new link. Links can be requested again after 10 minutes.";

const FAILURE =
    "We couldn't send a new link right now. Please try again in a moment.";

// "Send me a new link" on the invitation landing's invalid-link state (#438).
export const RequestNewInviteLink = () => {
    const { resendInvitation } = useInvitationApi();
    const isProcessing = useAtomValue(isProcessingInvitationAtom);
    const runGuarded = useSubmitGuard();
    const turnstile = useTurnstile();

    const [isSent, setIsSent] = useState(false);
    const [failure, setFailure] = useState<string | null>(null);

    const {
        register,
        handleSubmit,
        setError,
        formState: { errors },
    } = useForm<EmailFormData>({
        resolver: zodResolver(emailSchema),
    });

    const onSubmit = (data: EmailFormData) =>
        runGuarded(async () => {
            setFailure(null);

            try {
                await resendInvitation(data.email, turnstile.token);
                setIsSent(true);
            } catch (error) {
                // A captcha answer (OB-06) is checked first: its 400 says
                // nothing about the email. Any other 400 is the server's
                // verdict on the email itself; anything else says nothing
                // about it and must not read as sent.
                const captchaMessage = captchaErrorMessage(error);

                if (captchaMessage) {
                    setFailure(captchaMessage);
                } else if (asApiFailure(error)?.status === 400) {
                    setError("email", { message: INVALID_EMAIL });
                } else {
                    setFailure(FAILURE);
                }

                // The refused request spent the token; the next send needs a
                // fresh one. (A success replaces the form, so it needs no reset.)
                turnstile.reset();
            }
        });

    if (isSent) {
        return (
            <p role="status" className="mt-8 text-base">
                {CONFIRMATION}
            </p>
        );
    }

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="mt-8 rounded-2xl bg-base-200 p-6 sm:p-8 space-y-6"
        >
            <div>
                <label htmlFor="new-link-email" className="block font-bold mb-2">
                    Email address
                </label>
                <input
                    id="new-link-email"
                    type="email"
                    autoComplete="email"
                    className="input input-bordered w-full bg-white text-neutral"
                    {...register("email")}
                />
                {errors.email && (
                    <p className="text-red-500 text-sm mt-1">
                        {errors.email.message}
                    </p>
                )}
            </div>

            <TurnstileCheck turnstile={turnstile} />

            {failure && (
                <div role="alert" className="alert alert-error">
                    <span>{failure}</span>
                </div>
            )}

            <Button
                type="submit"
                color={ComponentColor.Warning}
                loading={isProcessing}
                disabled={turnstile.isAwaitingToken}
                aria-describedby={
                    turnstile.isAwaitingToken ? turnstile.hintId : undefined
                }
                className="text-neutral font-bold normal-case"
            >
                Send me a new link
            </Button>
        </form>
    );
};
